/**
 * WalletInstitutionDetail — the four-section institution detail page.
 *
 * Sections:
 *   1. About this institution      — identity + Validate FSP action
 *   2. Your relationship            — type, status, adviser + Validate Adviser
 *   3. Trusted communication details — official reference channels
 *   4. Your Altara interactions     — reuses existing decisions collection
 *
 * The four trust concepts stay VISUALLY DISTINCT:
 *   • Participating Institution     (green)
 *   • Regulatory record matched     (navy) — appears after Validate FSP
 *   • Associated with institution   (amber)
 *   • Received through Altara       (gold)
 *
 * The bottom CTA "Check this interaction" is a bridge to the future
 * Trust & Safety surface (not built here; routes to a coming-next state).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Building2, ShieldCheck, ShieldAlert, User,
  Globe, Mail, Phone, MessageCircle, LifeBuoy, Search, CheckCircle2,
  Loader2, ExternalLink, AlertTriangle, Landmark, Info,
} from "lucide-react";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import {
  getInstitutionDetail, validateFsp, validateAdviser,
} from "@/lib/wallet-trusted-institutions";
import { loadWallet } from "@/lib/wallet-storage";
import { statusLabel, zar } from "@/lib/wallet-decisions";
import { ParticipatingBadge } from "@/pages/wallet/WalletInstitutions";
import InfoBadge from "@/components/wallet/institutions/InfoBadge";

const CHANNEL_ICONS = {
  WEBSITE:         Globe,
  DOMAIN:          Globe,
  EMAIL_DOMAIN:    Mail,
  PHONE:           Phone,
  WHATSAPP:        MessageCircle,
  SMS_SENDER_ID:   MessageCircle,
  SUPPORT:         LifeBuoy,
  FRAUD_REPORTING: ShieldAlert,
};
const CHANNEL_LABELS = {
  WEBSITE:         "Official website",
  DOMAIN:          "Official domain",
  EMAIL_DOMAIN:    "Official email domain",
  PHONE:           "Official telephone",
  WHATSAPP:        "Official WhatsApp",
  SMS_SENDER_ID:   "Official SMS sender",
  SUPPORT:         "Support channel",
  FRAUD_REPORTING: "Fraud / suspicious communication contact",
};

export default function WalletInstitutionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const wallet = loadWallet() || {};
  const deviceId = wallet.device_id;

  const [data, setData]     = useState(null);
  const [err, setErr]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!deviceId) { nav("/wallet/onboarding", { replace: true }); return; }
    let c = false;
    (async () => {
      try { const d = await getInstitutionDetail(id, deviceId); if (!c) setData(d); }
      catch (e) { if (!c) setErr(e?.response?.data?.detail || "Could not load."); }
      finally { if (!c) setLoading(false); }
    })();
    return () => { c = true; };
  }, [id, deviceId, nav]);

  if (loading) return <Skeleton />;
  if (err || !data) return <ErrorState msg={err} />;

  const inst    = data.institution;
  const rel     = data.relationship;
  const reps    = data.representatives || [];
  const chans   = data.channels || [];
  const latest  = data.latest_interaction;

  return (
    <WalletShell activeTab="institutions">
      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-6 lg:py-8 space-y-6" data-testid="institution-detail">
        <Link to="/wallet/institutions" className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-500 hover:text-slate-800" data-testid="institution-detail-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to My Trusted Institutions
        </Link>

        {/* Header */}
        <header className="rounded-2xl border border-slate-100 bg-white p-5 lg:p-6" data-testid="inst-header">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
              <Building2 className="w-7 h-7" style={{ color: NAVY }} />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-2xl font-semibold" style={{ color: NAVY }}>
                {inst.trading_name || inst.legal_name}
              </h1>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <ParticipatingBadge />
                {data.regulatory_validation_institution && <RegulatoryMatchedBadge />}
              </div>
              <div className="text-[12.5px] text-slate-600 mt-2 leading-relaxed">
                Your relationship with {inst.trading_name || inst.legal_name} is connected to your Trust Wallet.
              </div>
            </div>
          </div>
        </header>

        {/* 1. About */}
        <AboutSection inst={inst} deviceId={deviceId} priorValidation={data.regulatory_validation_institution} onValidated={v => setData(d => ({ ...d, regulatory_validation_institution: v }))} />

        {/* 2. Relationship */}
        <RelationshipSection rel={rel} reps={reps} priorValidations={data.regulatory_validation_representatives || {}} institutionId={inst.institution_id} deviceId={deviceId} onValidated={(repId, v) => setData(d => ({ ...d, regulatory_validation_representatives: { ...(d.regulatory_validation_representatives || {}), [repId]: v } }))} />

        {/* 3. Trusted communication */}
        <TrustedChannelsSection channels={chans} />

        {/* 4. Altara interactions */}
        <AltaraInteractionsSection latest={latest} />

        {/* Bridge to Trust & Safety */}
        <CheckInteractionCTA institutionName={inst.trading_name || inst.legal_name} />
      </div>
    </WalletShell>
  );
}

/* ─────────────────────── section 1 ─────────────────────── */
function AboutSection({ inst, deviceId, priorValidation, onValidated }) {
  const [open, setOpen]     = useState(Boolean(priorValidation));
  const [result, setResult] = useState(priorValidation || null);
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState(null);

  async function runValidate() {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const v = await validateFsp(inst.institution_id, deviceId);
      setResult(v); setOpen(true); onValidated(v);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not validate."); }
    finally { setBusy(false); }
  }

  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-5 lg:p-6" data-testid="inst-about">
      <SectionTitle>About this institution</SectionTitle>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
        <Row k="Institution"       v={inst.trading_name || inst.legal_name} />
        <Row k="Legal name"        v={inst.legal_name} />
        <Row k="Type"              v={inst.institution_type} />
        <Row k="FSP number"        v={inst.regulatory_identifier ? `${inst.regulatory_identifier} (demo)` : "—"} />
        <Row k="Regulator"         v={inst.regulator} />
        <Row k="Participation"     v="Participating Institution" />
      </dl>

      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={runValidate}
          disabled={busy}
          data-testid="validate-fsp-btn"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12.5px] font-semibold border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
          style={{ color: NAVY }}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          {result ? "Re-run FSP validation" : "Validate FSP"}
        </button>
        <InfoBadge
          testid="regulatory-info-badge"
          title="Regulatory validation"
          body="In production, Altara can connect to available authoritative regulatory sources, such as relevant FSCA records in South Africa, to help validate participating financial institutions and advisers. This TechSprint demonstration uses controlled demo data."
        />
      </div>

      {open && result && (
        <ValidationPanel testid="fsp-validation-panel" heading="Financial Services Provider validation" subject={inst.trading_name || inst.legal_name}
          result={result}
          rows={[
            ["Institution name", result.matched_name],
            ["FSP number",       result.matched_identifier ? `${result.matched_identifier} (demo)` : "—"],
            ["Status",           result.regulatory_status],
            ["Regulatory source", result.source_reference],
          ]}
          liveExplanation="Altara can check relevant institution information against available authoritative regulatory records, such as FSCA records in South Africa, to help consumers confirm that the institution details they have been given correspond with regulatory information."
          boundaryNote="Regulatory validation helps establish whether institution information corresponds with available regulatory records. It does not authenticate every message, document, person or payment request claiming to come from that institution."
        />
      )}
      {err && <div className="text-[12px] text-red-700 mt-2" data-testid="validate-fsp-error">{err}</div>}
    </section>
  );
}

/* ─────────────────────── section 2 ─────────────────────── */
function RelationshipSection({ rel, reps, priorValidations, institutionId, deviceId, onValidated }) {
  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-5 lg:p-6" data-testid="inst-relationship">
      <SectionTitle>Your relationship</SectionTitle>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
        <Row k="Relationship" v={rel.relationship_type} />
        <Row k="Status"       v={rel.relationship_status === "ACTIVE" ? "Active" : rel.relationship_status} />
        <Row k="Established through" v="Altara" />
      </dl>

      {reps.length > 0 && (
        <div className="mt-5 space-y-3">
          {reps.map(rep => (
            <AdviserCard
              key={rep.representative_id}
              rep={rep}
              priorValidation={priorValidations[rep.representative_id]}
              onValidated={v => onValidated(rep.representative_id, v)}
              institutionId={institutionId}
              deviceId={deviceId}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function AdviserCard({ rep, priorValidation, onValidated, institutionId, deviceId }) {
  const [open, setOpen]     = useState(Boolean(priorValidation));
  const [result, setResult] = useState(priorValidation || null);
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState(null);

  async function runValidate() {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const v = await validateAdviser(institutionId, rep.representative_id, deviceId);
      setResult(v); setOpen(true); onValidated(v);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not validate."); }
    finally { setBusy(false); }
  }

  return (
    <div className="rounded-xl border border-slate-100 p-4" data-testid={`adviser-card-${rep.representative_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
          <User className="w-5 h-5" style={{ color: NAVY }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>{rep.name}</div>
          <div className="text-[12px] text-slate-600">{rep.role}</div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10.5px] font-semibold" style={{ background: `${GOLD}22`, color: NAVY }} data-testid="associated-badge"
              title="Altara has a relationship record associating this person with the institution.">
              Associated with institution
            </span>
            {result && <RepMatchedBadge />}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <button
            onClick={runValidate}
            disabled={busy}
            data-testid={`validate-adviser-btn-${rep.representative_id}`}
            className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11.5px] font-semibold border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
            style={{ color: NAVY }}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
            {result ? "Re-run" : "Validate Adviser"}
          </button>
        </div>
      </div>

      {open && result && (
        <ValidationPanel testid={`adviser-validation-panel-${rep.representative_id}`} heading="Adviser validation" subject={rep.name}
          result={result}
          rows={[
            ["Name",                    result.matched_name],
            ["Associated institution",  result.matched_institution],
            ["Representative status",   result.regulatory_status],
            ["Regulatory / reference source", result.source_reference],
          ]}
          liveExplanation="Where appropriate regulatory information is available, Altara can check adviser or representative information against relevant authoritative records and participating-institution information."
          boundaryNote="A matched adviser record helps confirm that the person's identity details correspond with available institutional or regulatory information. It does not establish that every phone call, WhatsApp message, email or document using that person's name actually came from them."
        />
      )}
      {err && <div className="text-[12px] text-red-700 mt-2">{err}</div>}
    </div>
  );
}

/* ─────────────────────── section 3 ─────────────────────── */
function TrustedChannelsSection({ channels }) {
  if (!channels.length) return null;
  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-5 lg:p-6" data-testid="inst-channels">
      <SectionTitle>Trusted communication details</SectionTitle>
      <p className="text-[12px] text-slate-600 leading-relaxed">
        Use these details as a reference when you receive an unexpected communication claiming to be from this institution.
      </p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {channels.map(ch => {
          const Icon = CHANNEL_ICONS[ch.channel_type] || Globe;
          const label = CHANNEL_LABELS[ch.channel_type] || ch.channel_type;
          const isFraud = ch.channel_type === "FRAUD_REPORTING";
          return (
            <div
              key={ch.channel_id}
              data-testid={`channel-${ch.channel_type.toLowerCase()}`}
              className="rounded-xl p-3 border"
              style={{ borderColor: isFraud ? "#FCA5A5" : "#E2E8F0", background: isFraud ? "#FEF2F2" : "#F8FAFC" }}
            >
              <div className="flex items-center gap-1.5">
                <Icon className="w-3.5 h-3.5" style={{ color: isFraud ? "#B91C1C" : NAVY }} />
                <div className="text-[10.5px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
              </div>
              <div className="mt-1 font-mono text-[12.5px] break-all" style={{ color: NAVY }}>{ch.channel_value}</div>
              <div className="mt-1 text-[10px] text-slate-500">Provided by institution</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ─────────────────────── section 4 ─────────────────────── */
function AltaraInteractionsSection({ latest }) {
  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-5 lg:p-6" data-testid="inst-interactions">
      <SectionTitle>Your Altara interactions</SectionTitle>
      <p className="text-[12px] text-slate-600 leading-relaxed">
        Financial interactions delivered through Altara provide an additional reference point for your relationship.
      </p>
      {!latest ? (
        <div className="mt-4 rounded-xl p-4 text-[12px] text-slate-500 border border-dashed border-slate-200">
          No Altara-delivered interactions yet.
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-slate-100 p-4 flex items-start gap-3" data-testid="inst-latest-interaction">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
            <Landmark className="w-5 h-5" style={{ color: NAVY }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-[14.5px] font-semibold" style={{ color: NAVY }}>{latest.proposal_name}</div>
            <div className="text-[11.5px] text-slate-600 mt-0.5">Investment Proposal · Received through Altara · {new Date(latest.sent_at).toLocaleDateString()}</div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold" style={{ background: `${GOLD}22`, color: NAVY }}>
                Received through Altara
              </span>
              <span className="text-[11px] text-slate-500">Status: <span className="font-semibold" style={{ color: NAVY }}>{statusLabel(latest.status)}</span></span>
              {latest.financial_commitment_zar ? <span className="text-[11px] text-slate-500">Commitment: <span className="font-mono">{zar(latest.financial_commitment_zar)}</span></span> : null}
            </div>
          </div>
          <Link
            to={`/wallet/decisions/${latest.decision_id}`}
            data-testid="inst-view-decision"
            className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11.5px] font-semibold text-white"
            style={{ background: NAVY }}
          >
            View decision <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      )}
    </section>
  );
}

/* ─────────────────────── Trust & Safety bridge ─────────────────────── */
function CheckInteractionCTA({ institutionName }) {
  const nav = useNavigate();
  return (
    <section
      className="rounded-2xl border p-5 flex items-start gap-3"
      style={{ background: "#FFFBEB", borderColor: "#FDE68A" }}
      data-testid="check-interaction-cta"
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-white">
        <AlertTriangle className="w-5 h-5" style={{ color: "#B45309" }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>
          Received something claiming to be from {institutionName}?
        </div>
        <div className="text-[12px] text-slate-600 mt-1 leading-relaxed">
          If a message, call, email or document doesn't look right, Altara can help you compare it with the trusted information available for your relationship.
        </div>
        <button
          onClick={() => nav("/wallet/institutions/coming-next")}
          data-testid="check-this-interaction-btn"
          className="mt-3 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12.5px] font-semibold text-white"
          style={{ background: NAVY }}
        >
          <ShieldCheck className="w-3.5 h-3.5" /> Check this interaction
        </button>
      </div>
    </section>
  );
}

/* ─────────────────────── shared UI ─────────────────────── */
function SectionTitle({ children }) {
  return <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold mb-3" style={{ color: GOLD }}>{children}</div>;
}
function Row({ k, v }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-[10.5px] uppercase tracking-widest text-slate-400 flex-shrink-0 w-32">{k}</dt>
      <dd className="text-[12.5px] font-medium truncate" style={{ color: NAVY }}>{v || "—"}</dd>
    </div>
  );
}

function RegulatoryMatchedBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10.5px] font-semibold"
      style={{ background: `${NAVY}12`, color: NAVY }}
      data-testid="regulatory-matched-badge"
      title="Institution information corresponds with the configured regulatory-validation source."
    >
      <CheckCircle2 className="w-3 h-3" /> Regulatory record matched
    </span>
  );
}
function RepMatchedBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10.5px] font-semibold"
      style={{ background: `${NAVY}12`, color: NAVY }}
      data-testid="rep-matched-badge"
    >
      <CheckCircle2 className="w-3 h-3" /> Record matched
    </span>
  );
}

function ValidationPanel({ heading, subject, result, rows, liveExplanation, boundaryNote, testid }) {
  const matched = result.validation_status === "MATCHED";
  return (
    <div className="mt-4 rounded-xl border p-4" style={{ background: matched ? "#F0FDF4" : "#F8FAFC", borderColor: matched ? "#BBF7D0" : "#E2E8F0" }} data-testid={testid}>
      <div className="flex items-center gap-2">
        {matched && <CheckCircle2 className="w-4 h-4" style={{ color: "#059669" }} />}
        <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: matched ? "#059669" : NAVY }}>
          {heading}
        </div>
      </div>
      <div className="mt-1 font-display text-[14.5px] font-semibold" style={{ color: NAVY }}>{subject}</div>
      <div className="mt-1 text-[13px] font-semibold" style={{ color: matched ? "#059669" : NAVY }}>
        {matched ? "✓ Record matched" : result.validation_status.replace(/_/g, " ")}
      </div>
      <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
        {rows.filter(r => r[1]).map(([k, v]) => (
          <div key={k} className="flex items-baseline gap-2">
            <dt className="text-[10.5px] uppercase tracking-widest text-slate-400 w-28 flex-shrink-0">{k}</dt>
            <dd className="text-[12px] truncate" style={{ color: NAVY }}>{v}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-3 rounded-lg bg-white border border-slate-100 p-3">
        <div className="text-[10.5px] uppercase tracking-widest font-semibold" style={{ color: GOLD }}>How this works when live</div>
        <div className="text-[11.5px] text-slate-600 mt-1 leading-relaxed">{liveExplanation}</div>
      </div>
      <div className="mt-2 flex items-start gap-2 text-[10.5px] text-slate-500 leading-relaxed">
        <Info className="w-3 h-3 flex-shrink-0 mt-0.5" /> {boundaryNote}
      </div>
    </div>
  );
}

/* ─────────────────────── skeleton / error ─────────────────────── */
function Skeleton() {
  return (
    <WalletShell activeTab="institutions">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-4" data-testid="institution-detail-loading">
        <div className="h-24 bg-white border border-slate-100 rounded-2xl animate-pulse" />
        <div className="h-40 bg-white border border-slate-100 rounded-2xl animate-pulse" />
        <div className="h-40 bg-white border border-slate-100 rounded-2xl animate-pulse" />
      </div>
    </WalletShell>
  );
}
function ErrorState({ msg }) {
  return (
    <WalletShell activeTab="institutions">
      <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="institution-detail-error">
        <ShieldAlert className="w-8 h-8 mx-auto text-slate-400" />
        <div className="mt-3 font-display text-lg font-semibold" style={{ color: NAVY }}>Institution unavailable</div>
        <div className="text-[13px] text-slate-500 mt-1">{msg}</div>
        <Link to="/wallet/institutions" className="mt-4 inline-flex items-center gap-1 text-[12px] font-medium" style={{ color: NAVY }}>
          ← Back to My Trusted Institutions
        </Link>
      </div>
    </WalletShell>
  );
}
